import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * AI Glow Component
 * Adds a subtle pulsing glow animation to AI-generated content
 * Communicates "living intelligence"
 */
export const AIGlow = ({ children, enabled = true, intensity = 'medium' }) => {
  if (!enabled) return children;

  // Intensity variants
  const intensityClasses = {
    low: 'animate-pulse-slow',
    medium: 'animate-pulse-medium',
    high: 'animate-pulse-fast',
  };

  return (
    <div className="relative group" data-testid="ai-glow-container">
      {/* Glow effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg opacity-20 blur group-hover:opacity-30 transition-opacity ${intensityClasses[intensity]}`}></div>
      
      {/* Content */}
      <div className="relative">
        {children}
      </div>
    </div>
  );
};

/**
 * AI Badge Component
 * Small badge indicating AI-generated content with sparkle icon
 */
export const AIBadge = ({ size = 'sm', pulse = true }) => {
  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1',
  };

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/40 ${sizeClasses[size]} ${pulse ? 'animate-pulse-slow' : ''}`}
      data-testid="ai-badge"
    >
      <Sparkles className="w-3 h-3 text-purple-400" />
      <span className="font-semibold text-purple-300">AI</span>
    </div>
  );
};

/**
 * AI Card Component
 * Pre-styled card wrapper with AI glow for recommendations, insights, etc.
 */
export const AICard = ({ children, title, badge = true, intensity = 'medium' }) => {
  return (
    <AIGlow enabled={true} intensity={intensity}>
      <div className="bg-[#0E1426]/80 backdrop-blur border border-[#22304A] rounded-lg p-4" data-testid="ai-card">
        {title && (
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {badge && <AIBadge size="sm" pulse={true} />}
          </div>
        )}
        <div className="text-gray-300">
          {children}
        </div>
      </div>
    </AIGlow>
  );
};

export default AIGlow;
